import React, { useEffect, useRef } from 'react';
import { createChart, UTCTimestamp, LineSeries, HistogramSeries,ColorType } from 'lightweight-charts';
import { OHLCData, calculateMACD } from '../composables/chartUtils';
import { useTheme } from '../context/ThemeContext'; // Import useTheme

interface MACDChartProps {
  data: OHLCData[];
  showOverlays: boolean; // Add this prop
  showDashed?: boolean; // Add this prop
  setChartRef?: (chart: any) => void;              // 🌟 เพิ่มบรรทัดนี้
  onTimeRangeChange?: (range: any) => void;     // 🌟 เพิ่มบรรทัดนี้
}

const MACDChart: React.FC<MACDChartProps> = ({ data, showOverlays, showDashed, setChartRef, onTimeRangeChange }) => {
    const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<any>(null);
  const { theme } = useTheme(); // Get the current theme

  useEffect(() => {
    if (!chartRef.current || !data.length) return;
    const width = chartRef.current.offsetWidth;
    const chart = createChart(chartRef.current, {
      width,
      height: 100,
      layout: {
        background: { type: ColorType.Solid, color: theme === 'dark' ? '#18181b' : '#ffffff' },
        textColor: theme === 'dark' ? '#e5e7eb' : '#000000', // Dynamic text color
      },
      grid: {
        vertLines: { color: theme === 'dark' ? '#27272a' : '#e0e0e0' },
        horzLines: { color: theme === 'dark' ? '#27272a' : '#e0e0e0' },
      },
      crosshair: { vertLine: { color: '#facc15', width: 1 }, horzLine: { color: '#facc15', width: 1 } },
      timeScale: { timeVisible: true },
    });
    chartInstance.current = chart;
    // 🌟 ซิงค์แกน X
    if (setChartRef) setChartRef(chart);
    const logicalRangeHandler = (logicalRange: any) => {
      if (onTimeRangeChange && logicalRange) onTimeRangeChange(logicalRange);
    };
    chart.timeScale().subscribeVisibleLogicalRangeChange(logicalRangeHandler);

    const macdData = calculateMACD(data);

    const macdSeries = chart.addSeries(LineSeries, { color: '#38bdf8', priceLineVisible: showDashed ? true : false, title: showOverlays ? 'MACD' : '' });
    const signalSeries = chart.addSeries(LineSeries, { color: '#fbbf24', priceLineVisible: showDashed ? true : false, title: showOverlays ? 'Signal' : '' });
    macdSeries.setData(macdData.map(d => ({ time: d.time as UTCTimestamp, value: d.macd })));
    signalSeries.setData(macdData.map(d => ({ time: d.time as UTCTimestamp, value: d.signal })));

    const histSeries = chart.addSeries(HistogramSeries, {
      color: '#a78bfa',
      priceLineVisible: showDashed ? true : false,
      priceFormat: { type: 'price', precision: 2, minMove: 0.01 },
    });
    histSeries.setData(macdData.map(d => ({ time: d.time as UTCTimestamp, value: d.hist })));

    const handleResize = () => {
      if (chartInstance.current && chartRef.current) {
        const width = chartRef.current.offsetWidth;
        if (width) chartInstance.current.applyOptions({ width });
      }
    };
    window.addEventListener('resize', handleResize);

    // 🌟 window 'resize' ยิงเฉพาะตอนขนาดหน้าต่างเบราว์เซอร์เปลี่ยน แต่การลาก resize
    // คอลัมน์ (grid resize) ไม่ทำให้ window resize เลยต้องมี ResizeObserver จับที่ตัว
    // container ของชาร์ตเองโดยตรงด้วย ถึงจะตามความกว้างของคอลัมน์ที่ลากได้จริง
    let observer: ResizeObserver | null = null;
    if (chartRef.current) {
      observer = new ResizeObserver(handleResize);
      observer.observe(chartRef.current);
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.timeScale().unsubscribeVisibleLogicalRangeChange(logicalRangeHandler); // 🌟 เพิ่มบรรทัดนี้
        chartInstance.current.remove();
        chartInstance.current = null;
      }
      if (setChartRef) setChartRef(null); // 🌟 เพิ่มบรรทัดนี้
      window.removeEventListener('resize', handleResize);
      if (observer && chartRef.current) observer.unobserve(chartRef.current);
    };
  }, [data, showOverlays, showDashed, theme]); // Add theme as a dependency

  return (
    <div className="w-full" style={{ minHeight: 100 }}>
      <div ref={chartRef} style={{ width: '100%', height: 100 }} />
    </div>
  );
};

export default MACDChart;